import type PQueue from "p-queue"
import { AsyncQueueLock } from "@/utils/lock"

export type PhotoExportingFlow = "export" | "publish"

export interface PhotoExportingBufferLease {
	release(): Promise<void>
}

let nextSessionNumber = 0

export class PhotoExportingCanceledError extends Error {
	constructor(message = "Photo exporting session was canceled") {
		super(message)
		this.name = "AbortError"
	}
}

export class PhotoExportingBusyError extends Error {
	constructor(message = "Photo exporting session is already active") {
		super(message)
		this.name = "PhotoExportingBusyError"
	}
}

type CancelHandler = () => void | Promise<void>

export class PhotoExportingSession {
	public readonly id: string
	public readonly flow: PhotoExportingFlow
	public readonly signal: AbortSignal

	private readonly controller = new AbortController()
	private readonly cancelHandlers = new Set<CancelHandler>()
	private readonly leases = new Set<PhotoExportingBufferLease>()
	private readonly trackedTasks = new Set<Promise<unknown>>()
	private readonly finalizePromise: Promise<void>
	private readonly cancelPromise: Promise<void>
	private resolveCanceled: () => void = () => undefined
	private resolveFinalized: () => void = () => undefined
	private canceled = false

	constructor(
		flow: PhotoExportingFlow,
		private readonly isCurrentSession: (session: PhotoExportingSession) => boolean,
	) {
		this.id = `${flow}-${++nextSessionNumber}`
		this.flow = flow
		this.signal = this.controller.signal
		this.finalizePromise = new Promise((resolve) => {
			this.resolveFinalized = resolve
		})
		this.cancelPromise = new Promise((resolve) => {
			this.resolveCanceled = resolve
		})
	}

	get isCanceled() {
		return this.canceled || this.signal.aborted
	}

	get finalized() {
		return this.finalizePromise
	}

	isCurrent() {
		return this.isCurrentSession(this)
	}

	throwIfCanceled() {
		if (this.isCanceled || !this.isCurrent()) {
			throw new PhotoExportingCanceledError()
		}
	}

	cancel() {
		if (this.canceled) {
			return
		}

		this.canceled = true
		this.controller.abort(new PhotoExportingCanceledError())
		this.resolveCanceled()
		for (const handler of Array.from(this.cancelHandlers)) {
			void Promise.resolve(handler()).catch((error) => {
				console.warn("[PhotoExportingSession] cancel handler failed", error)
			})
		}
	}

	onCancel(handler: CancelHandler) {
		this.cancelHandlers.add(handler)
		if (this.isCanceled) {
			void Promise.resolve(handler()).catch((error) => {
				console.warn("[PhotoExportingSession] cancel handler failed", error)
			})
		}

		return () => {
			this.cancelHandlers.delete(handler)
		}
	}

	trackQueue(queue: PQueue) {
		this.onCancel(() => {
			queue.clear()
		})
	}

	trackLease<T extends PhotoExportingBufferLease>(lease: T) {
		this.leases.add(lease)
		return lease
	}

	async releaseLease(lease?: PhotoExportingBufferLease) {
		if (!lease) {
			return
		}

		this.leases.delete(lease)
		await lease.release().catch((error) => {
			console.warn("[PhotoExportingSession] failed to release export buffer lease", error)
		})
	}

	async releaseAllLeases() {
		const leases = Array.from(this.leases)
		this.leases.clear()
		await Promise.all(
			leases.map((lease) =>
				lease.release().catch((error) => {
					console.warn(
						"[PhotoExportingSession] failed to release export buffer lease",
						error,
					)
				}),
			),
		)
	}

	trackTask<T>(task: Promise<T>) {
		const tracked = task.finally(() => {
			this.trackedTasks.delete(tracked)
		})
		this.trackedTasks.add(tracked)
		return tracked
	}

	async drainTrackedTasks() {
		while (this.trackedTasks.size > 0) {
			const tasks = Array.from(this.trackedTasks)
			await Promise.allSettled(tasks)
		}
	}

	async waitUnlessCanceled<T>(task: Promise<T>) {
		this.throwIfCanceled()
		return Promise.race([
			task,
			this.cancelPromise.then(() => {
				throw new PhotoExportingCanceledError()
			}),
		])
	}

	finish() {
		this.cancelHandlers.clear()
		this.resolveFinalized()
	}
}

class PhotoExportingSessionCoordinator {
	private readonly beginSessionLock = new AsyncQueueLock(0)
	private readonly rendererLock = new AsyncQueueLock(0)
	private currentSession: PhotoExportingSession | null = null

	async beginSession(flow: PhotoExportingFlow, options?: { rejectIfActive?: boolean }) {
		await this.beginSessionLock.lock()
		try {
			const existing = this.currentSession
			if (existing) {
				if (options?.rejectIfActive) {
					throw new PhotoExportingBusyError()
				}
				await existing.finalized
			}

			const session = new PhotoExportingSession(
				flow,
				(candidate) => this.currentSession === candidate,
			)
			this.currentSession = session
			return session
		} finally {
			this.beginSessionLock.unlock()
		}
	}

	cancelCurrent(flow?: PhotoExportingFlow) {
		const session = this.currentSession
		if (!session || (flow && session.flow !== flow)) {
			return
		}

		session.cancel()
	}

	async withRenderer<T>(
		session: PhotoExportingSession,
		task: () => Promise<T>,
		options?: { allowCanceled?: boolean },
	) {
		if (!options?.allowCanceled) {
			session.throwIfCanceled()
		} else if (!session.isCurrent()) {
			throw new PhotoExportingCanceledError()
		}

		await this.rendererLock.lock()
		try {
			if (!options?.allowCanceled) {
				session.throwIfCanceled()
			} else if (!session.isCurrent()) {
				throw new PhotoExportingCanceledError()
			}

			const result = await task()

			if (!options?.allowCanceled) {
				session.throwIfCanceled()
			} else if (!session.isCurrent()) {
				throw new PhotoExportingCanceledError()
			}

			return result
		} finally {
			this.rendererLock.unlock()
		}
	}

	finishSession(session: PhotoExportingSession) {
		if (this.currentSession === session) {
			this.currentSession = null
		}
		session.finish()
	}
}

export const photoExportingSession = new PhotoExportingSessionCoordinator()
